import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Plus, Edit2, Trash2, X, Loader2, CheckCircle,
  Package, Users, Layers, ToggleLeft, ToggleRight, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';
import { formatMoney } from '../../lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_TYPE_LABELS = {
  individual: { label: 'Individual', icon: Package, color: 'text-[#1A2E6E] bg-[#1A2E6E]/10' },
  group:      { label: 'Group',      icon: Users,   color: 'text-[#4CAF50] bg-[#4CAF50]/10' },
  both:       { label: 'Both',       icon: Layers,  color: 'text-[#F5A623] bg-[#F5A623]/10' },
};

const LEVEL_OPTIONS = ['A1-A2', 'B1', 'B2', 'C1-C2'];

const EMPTY_FORM = {
  name: '',
  slug: '',
  description: '',
  price_brl: '',
  currency: 'EUR',
  credits: '',
  validity_days: 30,
  plan_type: 'individual',
  max_students: '',
  sort_order: 0,
  recommended_for_levels: [],
};

const slugify = (str) =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const AdminPlans = () => {
  const { settings } = usePublicSettings();
  const defaultCurrency = settings.default_currency || 'EUR';
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen]   = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/plans?page_size=200`);
      setPlans(res.data.data ?? res.data);
    } catch {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingPlan(null);
    setForm({ ...EMPTY_FORM, currency: defaultCurrency });
    setModalOpen(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      name: plan.name,
      slug: plan.slug,
      description: plan.description || '',
      price_brl: plan.price_brl,
      currency: plan.currency || 'EUR',
      credits: plan.credits,
      validity_days: plan.validity_days,
      plan_type: plan.plan_type,
      max_students: plan.max_students ?? '',
      sort_order: plan.sort_order ?? 0,
      recommended_for_levels: plan.recommended_for_levels ?? [],
    });
    setModalOpen(true);
  };

  const handleNameChange = (val) => {
    setForm(f => ({
      ...f,
      name: val,
      slug: editingPlan ? f.slug : slugify(val),
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.slug.trim()) { toast.error('Slug is required'); return; }
    if (!form.price_brl || isNaN(parseFloat(form.price_brl))) { toast.error('Price must be a valid number'); return; }
    if (!form.credits || isNaN(parseInt(form.credits)) || parseInt(form.credits) < 1) {
      toast.error('Credits must be a positive integer');
      return;
    }

    setSaving(true);
    const payload = {
      ...form,
      price_brl: parseFloat(form.price_brl),
      currency: form.currency || 'EUR',
      credits: parseInt(form.credits),
      validity_days: parseInt(form.validity_days) || 30,
      sort_order: parseInt(form.sort_order) || 0,
      max_students: form.max_students !== '' ? parseInt(form.max_students) : null,
      recommended_for_levels: form.recommended_for_levels,
    };

    try {
      if (editingPlan) {
        await axios.put(`${API_URL}/api/admin/plans/${editingPlan.id}`, payload);
        toast.success('Plan updated');
      } else {
        await axios.post(`${API_URL}/api/admin/plans`, payload);
        toast.success('Plan created');
      }
      setModalOpen(false);
      fetchPlans();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join('; ')
          : 'Failed to save plan. Please check your inputs.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (plan) => {
    setTogglingId(plan.id);
    try {
      if (plan.is_active) {
        await axios.delete(`${API_URL}/api/admin/plans/${plan.id}`);
        toast.success('Plan deactivated');
      } else {
        await axios.put(`${API_URL}/api/admin/plans/${plan.id}`, { is_active: true });
        toast.success('Plan activated');
      }
      fetchPlans();
    } catch {
      toast.error('Failed to update plan status');
    } finally {
      setTogglingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">Plans</h1>
          <p className="text-[#1A2E6E]/60 mt-1">Manage subscription plans and packages</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors text-sm font-medium shrink-0"
        >
          <Plus size={16} /> New Plan
        </button>
      </div>

      {/* Plans grid */}
      {plans.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-12 text-center">
          <Package className="w-12 h-12 text-[#1A2E6E]/20 mx-auto mb-4" />
          <p className="text-[#1A2E6E]/60">No plans yet. Create your first plan to start enrolling students.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {plans.map((plan, i) => {
            const typeInfo = PLAN_TYPE_LABELS[plan.plan_type] || PLAN_TYPE_LABELS.individual;
            const TypeIcon = typeInfo.icon;
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className={`bg-white rounded-xl border overflow-hidden transition-all ${
                  plan.is_active ? 'border-[#E8EAF0]' : 'border-[#E8EAF0] opacity-50'
                }`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <h3 className="font-bold text-[#1A2E6E] text-base truncate">{plan.name}</h3>
                      <p className="text-xs text-[#1A2E6E]/40 mt-0.5">{plan.slug}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium shrink-0 ${typeInfo.color}`}>
                      <TypeIcon size={12} /> {typeInfo.label}
                    </span>
                  </div>

                  {plan.description && (
                    <p className="text-sm text-[#1A2E6E]/60 mb-3 line-clamp-2">{plan.description}</p>
                  )}

                  <div className={`grid gap-2 text-center mb-4 ${plan.max_students ? 'grid-cols-4' : 'grid-cols-3'}`}>
                    <div className="bg-[#F8F9FC] rounded-lg p-2">
                      <p className="text-lg font-bold text-[#1A2E6E]">
                        {formatMoney(plan.price_brl, plan.currency)}
                      </p>
                      <p className="text-[10px] text-[#1A2E6E]/50">Price</p>
                    </div>
                    <div className="bg-[#F8F9FC] rounded-lg p-2">
                      <p className="text-lg font-bold text-[#1A2E6E]">{plan.credits}</p>
                      <p className="text-[10px] text-[#1A2E6E]/50">Lessons</p>
                    </div>
                    <div className="bg-[#F8F9FC] rounded-lg p-2">
                      <p className="text-lg font-bold text-[#1A2E6E]">{plan.validity_days}d</p>
                      <p className="text-[10px] text-[#1A2E6E]/50">Valid</p>
                    </div>
                    {plan.max_students && (
                      <div className="bg-[#4CAF50]/8 rounded-lg p-2">
                        <p className="text-lg font-bold text-[#4CAF50]">{plan.max_students}</p>
                        <p className="text-[10px] text-[#1A2E6E]/50">Max</p>
                      </div>
                    )}
                  </div>

                  {plan.recommended_for_levels?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {plan.recommended_for_levels.map(level => (
                        <span key={level} className="px-2 py-0.5 rounded-md bg-[#F5A623]/10 text-[#C97D0A] text-[10px] font-semibold">
                          {level}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEdit(plan)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-[#E8EAF0] text-[#1A2E6E] rounded-lg hover:bg-[#F5F7FA] transition-colors text-sm font-medium"
                    >
                      <Edit2 size={14} /> Edit
                    </button>
                    <button
                      onClick={() => toggleActive(plan)}
                      disabled={togglingId === plan.id}
                      title={plan.is_active ? 'Deactivate plan' : 'Activate plan'}
                      className={`p-2 rounded-lg transition-colors ${
                        plan.is_active
                          ? 'text-[#4CAF50] hover:bg-[#4CAF50]/10'
                          : 'text-[#1A2E6E]/40 hover:bg-[#1A2E6E]/5'
                      }`}
                    >
                      {togglingId === plan.id
                        ? <Loader2 size={18} className="animate-spin" />
                        : plan.is_active ? <ToggleRight size={22} /> : <ToggleLeft size={22} />
                      }
                    </button>
                  </div>
                </div>

                {!plan.is_active && (
                  <div className="px-5 py-2 bg-[#F5F5F5] border-t border-[#E8EAF0] flex items-center gap-1.5 text-xs text-[#1A2E6E]/50">
                    <AlertCircle size={12} /> Inactive — not shown to students
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <h2 className="text-lg font-bold text-[#1A2E6E]">
                  {editingPlan ? 'Edit Plan' : 'New Plan'}
                </h2>
                <button onClick={() => setModalOpen(false)} className="p-2 hover:bg-[#1A2E6E]/5 rounded-lg">
                  <X size={20} className="text-[#1A2E6E]/60" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Plan Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => handleNameChange(e.target.value)}
                    placeholder="e.g. Monthly 8 Lessons"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">
                    Slug * <span className="text-[#1A2E6E]/40 font-normal">(URL identifier, unique)</span>
                  </label>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={e => setForm(f => ({ ...f, slug: slugify(e.target.value) }))}
                    placeholder="e.g. monthly-8-individual"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none font-mono text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Description <span className="text-[#1A2E6E]/40 font-normal">(optional)</span></label>
                  <textarea
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    rows={2}
                    placeholder="Brief description shown to students"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none resize-none text-sm"
                  />
                </div>

                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Price *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.price_brl}
                      onChange={e => setForm(f => ({ ...f, price_brl: e.target.value }))}
                      placeholder="320.00"
                      className="w-full px-3 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Currency</label>
                    <select
                      value={form.currency}
                      onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                      className="w-full px-3 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
                    >
                      <option value="EUR">EUR (€)</option>
                      <option value="BRL">BRL (R$)</option>
                      <option value="USD">USD ($)</option>
                      <option value="GBP">GBP (£)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Lessons *</label>
                    <input
                      type="number"
                      min="1"
                      value={form.credits}
                      onChange={e => setForm(f => ({ ...f, credits: e.target.value }))}
                      placeholder="8"
                      className="w-full px-3 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Valid (days)</label>
                    <input
                      type="number"
                      min="1"
                      value={form.validity_days}
                      onChange={e => setForm(f => ({ ...f, validity_days: e.target.value }))}
                      className="w-full px-3 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Plan Type *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(PLAN_TYPE_LABELS).map(([key, info]) => {
                      const Icon = info.icon;
                      const active = form.plan_type === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setForm(f => ({ ...f, plan_type: key }))}
                          className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border text-xs font-medium transition-all ${
                            active
                              ? 'border-[#1A2E6E] bg-[#1A2E6E]/5 text-[#1A2E6E]'
                              : 'border-[#E8EAF0] text-[#1A2E6E]/50 hover:border-[#1A2E6E]/30'
                          }`}
                        >
                          <Icon size={18} />
                          {info.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {(form.plan_type === 'group' || form.plan_type === 'both') && (
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-1">
                      Max Students <span className="text-[#1A2E6E]/40 font-normal">(group cap, optional)</span>
                    </label>
                    <input
                      type="number"
                      min="2"
                      value={form.max_students}
                      onChange={e => setForm(f => ({ ...f, max_students: e.target.value }))}
                      placeholder="e.g. 6"
                      className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Sort Order <span className="text-[#1A2E6E]/40 font-normal">(lower = first)</span></label>
                  <input
                    type="number"
                    min="0"
                    value={form.sort_order}
                    onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                    Recommended for levels <span className="text-[#1A2E6E]/40 font-normal">(pre-selects this plan in checkout)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {LEVEL_OPTIONS.map(level => {
                      const checked = form.recommended_for_levels.includes(level);
                      return (
                        <button
                          key={level}
                          type="button"
                          onClick={() => setForm(f => ({
                            ...f,
                            recommended_for_levels: checked
                              ? f.recommended_for_levels.filter(l => l !== level)
                              : [...f.recommended_for_levels, level],
                          }))}
                          className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                            checked
                              ? 'border-[#F5A623] bg-[#F5A623]/10 text-[#C97D0A]'
                              : 'border-[#E8EAF0] text-[#1A2E6E]/50 hover:border-[#1A2E6E]/30 hover:text-[#1A2E6E]'
                          }`}
                        >
                          {level}
                        </button>
                      );
                    })}
                  </div>
                  {form.recommended_for_levels.length > 0 && (
                    <p className="text-xs text-[#1A2E6E]/40 mt-2">
                      Students at these levels will see this plan pre-selected in checkout.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-3 p-6 border-t border-[#E8EAF0]">
                <button
                  onClick={() => setModalOpen(false)}
                  className="flex-1 py-3 rounded-xl border border-[#E8EAF0] text-[#1A2E6E] hover:bg-[#F5F7FA] transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl bg-[#1A2E6E] text-white hover:bg-[#16275C] transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                  {editingPlan ? 'Save Changes' : 'Create Plan'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPlans;
